// THE LONG KINDLING — Room 4: "The Midwife's cottage" (Act I) — the failing watch; the comfort you
// must take back. Authored from research/wall/script/v3/rooms_01-05.md §Room 4 and BUILD_PLAN.md,
// copying the proven hearth_hall.ts pattern (field names, fx/fire wiring).
//
// Painting public/art/midwife.png is 384x216; the room is 480x270 (ART→ROOM x1.25). The painting is a
// cool, clean, scrubbed room (daylight through the left window) with NO visible flame, so there is no
// baked fire cutout here; the small good fire is read in prose only and the sole fire fx is the far cold
// watch-fire glimpsed dim through the window toward the dark treeline.
//
// No prop-lock. The COMFORT-BUNDLE is CARRY-AND-RETURN only (a reversible toggle `bundle_taken`) —
// NOT a critical-path key, so it can never soft-lock (per cross-room note: keep it optional/return-only;
// R17 owns its payoff). Mother Sael is a Hush who polices her own mercy ("we don't"); the two empty
// cradles are the failing watch told flat.
import { loadRoom, type Room } from "../../core/schema";

export const midwife = {
  id: "midwife",
  name: "The Midwife's cottage",
  size: { w: 480, h: 270 },
  background: { color: 0x1a120a },
  bgImage: "art/midwife.png",
  // hero starts on the scrubbed floor, near the door (the comfort-bundle hangs on the hook by it)
  spawn: { x: 320, y: 244 },
  actorRadius: 8,
  interactRadius: 64,
  // the bundle's two states: hung ready by the door (right/default) vs lifted toward the door (carry-and-return).
  state: { bundle_taken: false },
  walkmesh: [
    [ { x: 40, y: 224 }, { x: 452, y: 224 }, { x: 452, y: 266 }, { x: 36, y: 266 } ],
  ],
  hotspots: [
    // ---- THE COMFORT-BUNDLE on the hook — carry-and-return only (reversible toggle; NEVER a gate key, no soft-lock)
    { id: "comfort_bundle", verb: "use", label: "the comfort-bundle", interactAt: { x: 332, y: 244 },
      line: "Linen, salve, a sip of brandy, tied and tagged: for the one at the edge. Mother Sael keeps it made up. 'It's never too late for a little comfort,' the tag says. Nobody's ever carried it out. You can lift it — but mind you bring it back by dawn. Don't you leave it on him.",
      effect: { toggle: ["bundle_taken"] },
      polygon: [ { x: 308, y: 116 }, { x: 356, y: 116 }, { x: 356, y: 168 }, { x: 308, y: 168 } ] },

    // ---- the two empty cradles — the human cost of the failing watch, told flat (look-at only)
    { id: "two_cradles", verb: "examine", label: "the two empty cradles", interactAt: { x: 232, y: 244 },
      line: "Two cradles, made ready last winter, never filled. Sael doesn't talk about it. The watch was stretched too thin on the relay; two men were lost holding the line; and what they were holding back came a little nearer that season, and two that should have lived didn't.",
      polygon: [ { x: 206, y: 182 }, { x: 262, y: 182 }, { x: 262, y: 216 }, { x: 206, y: 216 } ] },

    // ---- the salve-shelf / herbs — real medicine, no charms (look-at only)
    { id: "salve_shelf", verb: "examine", label: "the salve-shelf", interactAt: { x: 408, y: 244 },
      line: "Real medicine, no charms. Sael's the one soul in Wardlow who'll tell you straight there's no luck in it, only clean hands and willow-bark. Makes it stranger that she keeps the bundle by the door.",
      polygon: [ { x: 392, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 196 }, { x: 392, y: 196 } ] },

    // ---- the birthing-stool — the quiet rhyme of the three hard seats (look-at only)
    { id: "birthing_stool", verb: "examine", label: "the birthing-stool", interactAt: { x: 168, y: 244 },
      line: "Worn smooth as the keeper's stool up the hall, and the watcher's lonely seat out at the trees. Three seats in this village get sat in for the hardest work there is — one to bring them into the warm, one to keep the hall warm, and one out in the cold to keep the wood watched.",
      polygon: [ { x: 146, y: 192 }, { x: 192, y: 192 }, { x: 192, y: 218 }, { x: 146, y: 218 } ] },

    // ---- the salve & clean linen — the craft of the room (look-at only)
    { id: "clean_linen", verb: "examine", label: "the clean linen", interactAt: { x: 96, y: 242 },
      line: "Clean linen folded in stacks, herb-hung and dry. The most competent, least superstitious room in Wardlow — where life is fought for, plainly, with clean hands. A low good fire is kept for the work; clean warmth, the warm the watch is for. The cold is only the one place, out at the trees.",
      polygon: [ { x: 70, y: 150 }, { x: 118, y: 150 }, { x: 118, y: 206 }, { x: 70, y: 206 } ] },
  ],
  // living-scene fx: the painting shows no flame (a cool scrubbed room, daylight at the window), so the
  // only fire effect is the far COLD watch-fire glimpsed dim through the left window toward the dark
  // treeline — a small, dim, OTHER point of light.
  fx: [
    { id: "watchfire_far", kind: "glow", layer: "back", x: 36, y: 80, w: 12, h: 12, color: 0xff8a3c, intensity: 0.35, seed: 4 },
  ],
  npcs: [
    // Mother Sael — capable, dry, unsentimental except about the one bundle; a Hush who polices her own mercy.
    { id: "sael", x: 244, y: 240, radius: 9, color: 0x6e8b6e, label: "Mother Sael",
      dialog: [
        { text: "I bring them in and I lay them out, lad, and I've no time for charms in between. Clean hands. Willow for the fever. That's all the magic there is.", mood: "neutral" },
        { text: "The bundle? ...Some nights it's so cold out there, and he's an old man, and he's ours. It's never too late for a bit of comfort.", mood: "sad" },
        { text: "Why's it never gone out? ...Because I bring it back by dawn. Every time I near carry it out to him, I bring it back. Don't ask me why. I just do. We don't.", mood: "wary" },
        { text: "Slow the felling, if you've any say. We're burying the cutters faster than the cold at the trees ever took anyone.", mood: "wary" },
      ] },
  ],
  // Exit to the Green & Well (R7) — wired when R7 lands; commented so the world boots clean.
  exits: [
    // the door -> the Green & Well (R7)
    { id: "to_green", target: "green_well", spawn: { x: 240, y: 244 }, transition: "fade",
      polygon: [ { x: 296, y: 226 }, { x: 348, y: 226 }, { x: 348, y: 250 }, { x: 296, y: 250 } ] },
  ],
};

export const room: Room = loadRoom(midwife);
